<template>
  <div class="fare-option-container box" style="border: 1px solid #e3e2e2;">
    <NDCTag class="mt-2"></NDCTag>

    <h3 class="is-size-5 has-text-bold">
      {{ fareDescription }}
      <div
        v-if="fareDetails.length > 0"
        @click="showDetails = true"
        class="is-size-7 is-clickable has-text-success"
      >
        <b-icon
          class="is-size-7 is-underlined"
          icon="information"
          size="is-small"
        />
        view details
      </div>
    </h3>

    <b-modal
      v-model="showDetails"
      has-modal-card
      trap-focus
      :destroy-on-hide="false"
      close-button-aria-label="Close"
      aria-modal
      :width="600"
    >
      <div class="card p-3" style="width:580px">
        <div class="is-flex is-flex-direction-row">
          <div class="is-flex-grow-5">
            <h3 class="is-size-5 has-text-bold mb-2">{{ fareDescription }}</h3>
          </div>
          <button
            type="button"
            aria-label="Close message"
            class="delete is-flex-grow-0"
            @click="showDetails = false"
          ></button>
        </div>

        <div v-for="(detail, index) in fareDetails" :key="index">
          <ul class="is-size-7 has-text-grey">
            <li>{{ detail.DescText }}</li>
          </ul>
        </div>
      </div>
    </b-modal>

    <div class="is-size-5 has-text-grey">
      {{ offer.pricing.total | formatPriceFromAmount(0, offer.pricing.currency) }}
    </div>

    <OutOfPolicyTag
      v-if="rulesTriggered > 0"
      class="mt-1"
      position="is-right"
      :travelPolicy="travelPolicy"
    />

    <hr />
    <div class="is-size-7">
      <b-icon icon="information" size="is-small" />
      Cabin: {{ airCabinName }}
    </div>
    <div v-if="isRefundable" class="is-size-7 has-text-success has-text-bold">
      <b-icon icon="check-circle" size="is-small" type="is-success" />
      REFUNDABLE
    </div>
    <h3 class="is-size-7 has-text-success" v-if="firstJourney.baggage">
      <b-icon icon="bag-suitcase" size="is-small" />
      {{ firstJourney.baggage.text }}
      <span v-if="firstJourney.baggage.qty"
        >qty: {{ firstJourney.baggage.qty }}</span
      >
      <span v-if="firstJourney.baggage.weight">
        weight: {{ firstJourney.baggage.weight
        }}<span v-if="firstJourney.baggage.unit">{{
          firstJourney.baggage.unit
        }}</span></span
      >
    </h3>
    <div v-for="(service, serviceIndex) in services" :key="serviceIndex">
      <FareFamilyService :service="service" />
    </div>
    <div class="is-size-7">
      <b-icon icon="information" size="is-small" />
      Fare Rules:
      <span v-if="fareInfo.endorsements.length == 0"> None</span>
      <span
        v-for="(endorsement, eIndex) in fareInfo.endorsements"
        :key="eIndex"
      >
        {{ endorsement }}
      </span>
    </div>
    <hr />

    <div ref="journeyList" class="columns is-multiline is-mobile">
      <div v-for="journey in journeyList" :key="journey.id" class="column">
        <div class="columns is-gapless is-mobile mb-0">
          <div class="column is-narrow mr-2">
            <div class="has-text-weight-bold">{{ journey.origin }}</div>
          </div>
          <div class="column is-narrow mr-2">
            <span class="is-size-7 mt-1 is-block"
              ><span class="icon has-text-grey-light is-small"
                ><i class="mdi mdi-arrow-right"></i></span
            ></span>
          </div>
          <div class="column is-narrow mr-2">
            <div class="has-text-weight-bold">{{ journey.destination }}</div>
          </div>
          <!---->
        </div>
        <h3 class="is-size-7 has-text-bold">{{ journey.bookingClass }}</h3>
      </div>
    </div>

    <div
      v-if="
        flight.isSelectable && flight.immediateTravelBookingAdvice != 'prevent'
      "
      class="is-inline-block is-clickable"
      style="width: 140px;"
    >
      <b-button type="is-primary" expanded @click="checkSubmission()"
        >Book</b-button
      >
    </div>
    <div
      v-if="!flight.isSelectable"
      class="mt-4 is-size-7 has-text-grey is-block"
    >
      Not selectable
    </div>

    <div
      v-if="
        flight.isSelectable && flight.immediateTravelBookingAdvice == 'prevent'
      "
      class="mt-4 notification is-size-7 has-text-grey has-text-centered"
    >
      To book this option call: <br />
      877-924-0303
    </div>
  </div>
</template>

<script>
import bookingMixin from "@/mixins/bookingMixin"
import FareFamilyService from "./FareFamilyService.vue"
import AddMeetingGuest from "../../Meetings/AddMeetingGuest.vue"
import meetingsMixin from "@/mixins/meetingsMixin"
import OutOfPolicyTag from "../../Tags/OutOfPolicyTag.vue"
import NDCTag from "@/components/Tags/NDCTag.vue"
import NDCPriceCheckModal from "./NDCPriceCheckModal.vue"

export default {
  name: "NDCFareOption",
  mixins: [bookingMixin, meetingsMixin],
  props: {
    flight: Object,
    offer: Object,
    guest: Object,
  },
  components: {
    FareFamilyService,
    OutOfPolicyTag,
    NDCTag,
    NDCPriceCheckModal, // eslint-disable-line vue/no-unused-components
  },
  data: function() {
    return {
      showDetails: false,
      showPriceCheckModal: false,
    }
  },
  computed: {
    useAtcShopper() {
      return this.$store.state.bookTravel.atc?.useAtcShopper
    },
    offerInfo() {
      return this.offer
    },
    travelPolicy() {
      return this.flight?.travelPolicy || {}
    },
    rulesTriggered() {
      return this.travelPolicy?.rulesTriggered?.length || 0
    },
    isRefundable() {
      //Show refundable if fareInfo.refundable is true or if the fareInfo.endorsements contains the word "refundable"
      return this.offer.isRefundable
    },
    journeyList() {
      return this.offer.journeyList
    },
    fareDescription() {
      //Get the booking class from the first journey
      return this.firstJourney.bookingClass
    },
    fareInfo() {
      //Fare rules are included per segment, so we just show the first segment's fare rules
      return this.offer.fareRules[0]
    },
    fareDetails() {
      //Get the booking class from the first journey
      return this.firstJourney.details || []
    },
    services() {
      //Show any services that are included in the fare
      let services = this.offer.services.filter(
        (service) => service?.status == "included"
      )
      //Remove duplicates
      return [...new Map(services.map((m) => [m.description, m])).values()]
    },
    firstJourney() {
      return this.offer.journeyList[0]
    },
    airCabinName() {
      const cabins = this.$store.state.options.airCabins

      const cabinNames = this.offer.cabin
        .map((cabinCode) => {
          // Check if the cabinCode exists in cabins and get its name, default to the cabinCode
          const cabinName = cabins[cabinCode]
            ? cabins[cabinCode].toUpperCase()
            : cabinCode
          // If the name is found, format as "NAME (CODE)", otherwise just return the code
          return cabins[cabinCode] ? `${cabinName} (${cabinCode})` : cabinCode
        })
        .join(", ") // Join all formatted names with commas

      return cabinNames
    },
  },
  methods: {
    checkSubmission() {
      // console.log('checkSubmission', sol);

      if (this.flight.immediateTravelBookingAdvice == "warn") {
        this.$buefy.dialog.confirm({
          message:
            "You have selected a flight with a departure time in the next few hours. Please watch your email for your Final Ticketed Itinerary.  If you do not receive your Final Ticketed Itinerary, please contact Gant Travel Support at 877-924-0303 for assistance.   ",
          confirmText: "Continue booking",
          type: "is-info",
          hasIcon: true,
          onConfirm: () => {
            if (this.useAtcShopper) {
              //NDC Exchanges - Verify exchange price before checkout
              this.openPriceCheckModal()
            } else {
              this.submit()
            }
          },
        })
      } else {
        if (this.useAtcShopper) {
          //NDC Exchanges - Verify exchange price before checkout
          this.openPriceCheckModal()
        } else {
          this.submit()
        }
      }
    },
    openPriceCheckModal() {
      this.$buefy.modal.open({
        parent: this,
        component: NDCPriceCheckModal,
        props: {
          flight: this.flight,
          offer: this.offer,
          guest: this.guest,
        },
        hasModalCard: true,
        trapFocus: true,
        width: 600,
        canCancel: ["escape", "x"],
        events: {
          accept: (data) => {
            // Price check was accepted, proceed with booking
            this.$nav.pushRouteTo({ name: "ExchangeCheckout" })
          },
          reject: () => {
            // Price check was rejected, modal closes
          },
        },
        onCancel: () => {
          // Modal was cancelled/rejected
        },
      })
    },
    submit() {
      let payload = {
        flight: this.flight,
        offer: this.offer,
      }

      // Keep pricedOffer on the fare actually picked, so the seat map asks
      // about this offer's cabin instead of the originally priced one.
      if (this.offer?.offerId) {
        payload.pricedOffer = {
          offerId: this.offer.offerId,
          ownerCode: this.offer.ownerCode,
          responseId: this.offer.responseId,
        }
      }

      if (this.guest) {
        payload.guestId = this.guest.meetingAttendeeGuestId
      }

      this.$store.dispatch("bookTravel/setNdcBooking", payload)

      if (this.pathMixin_isMeeting) {
        this.meetingRouter()

        if (this.meetingInfo.allowedToBringGuest && !this.guest) {
          this.$buefy.modal.open({
            parent: this,
            component: AddMeetingGuest,
            props: {},
            hasModalCard: true,
            trapFocus: true,
            width: 650,
          })
        }
      } else if (this.$store.state.bookTravel.atc?.useAtcShopper) {
        this.$nav.pushRouteTo({ name: "ExchangeCheckout" })
      } else {
        this.$nav.pushRouteTo({ name: "BookingItinerary" })
      }
    },
  },
}
</script>

<style lang="scss" scoped></style>
